import {Col, css, DSIconBeast, DSIconBeastSlain, IconProps, Row, Text} from "@cdx/ds";
import {TooltipForChild} from "@cdx/common";
import {iconStyles} from "@cdx/ds/components/DSIcon/DSIcon.css";
import {CSSProperties} from "react";
import {Account} from "../../cdx-models/Account";
import {Card} from "../../cdx-models/Card";
import {beastStyles} from "./card.css";
import useFeatureFlag, {FEATURE_FLAGS} from "../useFeatureFlag";
import {useRoot} from "../../lib/mate/mate-utils";
import {toRomanNumeral} from "../../features/onboarding/onboarding-utils";

const Icon = (props: Pick<IconProps, "size" | "className"> & {isDone?: boolean}) => {
  const {size, className, isDone} = props;
  const iconProps = {size, className, inline: true};
  return isDone ? <DSIconBeastSlain {...iconProps} /> : <DSIconBeast {...iconProps} />;
};

const interpolate = (start: number, end: number, ratio: number) => {
  return start * (1 - ratio) + end * ratio;
};

const notDoneStyle = (level: number) => {
  const ratioDark = 1 - Math.exp(-level / 4);
  const ratioHue = 1 - Math.exp(-level / 10);

  return {
    background: `hsl(${interpolate(357, 320, ratioHue)} ${interpolate(64, 40, ratioDark)}% ${interpolate(70, 30, ratioDark)}%)`,
    color: `hsl(${interpolate(357, 320, ratioHue)} ${interpolate(52, 30, ratioDark)}% ${interpolate(87, 85, ratioDark)}%)`,
  };
};

export const BeastPill = ({
  level,
  style,
  size = "md",
  isDone,
  isNewUntil,
}: BeastProps & {style?: CSSProperties}) => {
  const isNext = useFeatureFlag(useRoot().account, FEATURE_FLAGS.nonRomanBeasts);
  return (
    <TooltipForChild
      tooltip={`Beast level ${level}. The level increases if a card moves from Run to Run without being completed.${isNewUntil ? " Is marked as 'New' as it was added into a started Sprint." : ""}`}
      placement="bottom"
    >
      <Row
        rounded={4}
        align="center"
        justify="center"
        pointerEvents="auto"
        px={size === "md" ? "4px" : "12px"}
        py={size === "md" ? "4px" : "6px"}
        minWidth="28px"
        {...(isNext
          ? {
              className: isDone ? beastStyles.type.done : undefined,
              style: isDone ? style : {...notDoneStyle(level), ...style},
            }
          : {
              className: isDone ? beastStyles.type.done : beastStyles.type.notDone,
              style,
            })}
        elevation={100}
        lineHeight="none"
        flex="none"
      >
        <Text
          type={size === "md" ? "label11Caps" : "label16"}
          noOverflow
          className={css({whiteSpace: "nowrap"})}
          variantNumeric="tabularNums"
        >
          {size === "md" ? (
            <Icon size={16} className={iconStyles.setSize["12px"]} isDone={isDone} />
          ) : (
            <Icon size={20} isDone={isDone} />
          )}{" "}
          {isNewUntil ? "NEW" : isNext ? level : toRomanNumeral(level)}
        </Text>
      </Row>
    </TooltipForChild>
  );
};

type BeastProps = {
  level: number;
  size?: "md" | "lg";
  isDone?: boolean;
  isNewUntil?: string | null;
};
const Beast = (props: BeastProps) => (
  <Col
    absolute
    left="0"
    right="0"
    bottom="0"
    align="center"
    zIndex={1}
    pointerEvents="none"
    style={{marginBottom: 0}}
  >
    <BeastPill style={{marginBottom: props.size === "lg" ? -12 : -8}} {...props} />
  </Col>
);

export const getBeastLevel = (account: Account, card: Card) => {
  // const beastLevel = card.createdAt.getTime() % 2 && card.createdAt.getTime() % 5;
  // return beastLevel;
  if (process.env.REACT_APP_MODE === "open") return 0;
  if (card.isDoc) return 0;
  return card.meta?.sprintLockedIn?.level || 0;
};

export default Beast;
